import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "item", "empty", "section", "prompt", "narrow", "registerLink", "expand", "fallback"];
  static values = {
    requireQuery: Boolean,
    minQueryLength: { type: Number, default: 0 },
    expanded: Boolean,
  };

  connect() {
    this.itemTargets.forEach((item, position) => {
      item.dataset.originalPosition ??= position;
    });
    this.originalOrder = [...this.itemTargets].sort((a, b) => a.dataset.originalPosition - b.dataset.originalPosition);
    this.listEnds = new Map(this.itemTargets.map((item) => [item.parentElement, item.nextSibling]));
    this.searchTerms = new Map(this.itemTargets.map((item) => [item, this.searchTermsFor(item)]));
    this.searchesModelNames = this.itemTargets.some((item) => "modelNames" in item.dataset);
    this.filter();
    this.moveCursorToEnd();
  }

  moveCursorToEnd() {
    if (!this.hasInputTarget) return;
    const el = this.inputTarget;
    if (el.value.length === 0) return;
    requestAnimationFrame(() => {
      const len = el.value.length;
      try {
        el.setSelectionRange(len, len);
      } catch {}
    });
  }

  filter() {
    const rawQuery = this.inputTarget.value;
    const tokens = this.tokenize(rawQuery);
    const queryLength = tokens.join("").length;
    const belowMin = queryLength < this.minQueryLengthValue;
    const showItems = (!this.requireQueryValue || tokens.length > 0) && !belowMin;

    const matches = this.itemTargets.map((item) => {
      if (!showItems) return false;
      if (tokens.length === 0 && this.isCollapsed(item)) return false;
      return tokens.length === 0 || this.matchesNameOrModel(item, tokens);
    });
    const matchCount = matches.filter(Boolean).length;

    this.itemTargets.forEach((item, idx) => {
      item.hidden = !matches[idx];
    });
    this.putNameMatchesFirst(tokens);
    this.sectionTargets.forEach((section) => {
      const hasVisibleItem = this.itemTargets.some((item) => !item.hidden && section.contains(item));
      section.hidden = !hasVisibleItem;
    });
    if (this.hasPromptTarget) {
      this.promptTarget.hidden = queryLength > 0;
    }
    if (this.hasNarrowTarget) {
      this.narrowTarget.hidden = !belowMin || queryLength === 0;
    }
    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = matchCount > 0 || belowMin;
    }
    this.expandTargets.forEach((expand) => {
      expand.hidden = this.expandedValue || tokens.length > 0;
    });
    this.fallbackTargets.forEach((fallback) => {
      fallback.hidden = tokens.length === 0 && this.isCollapsed(fallback);
    });
    this.registerLinkTargets.forEach((link) => {
      const base = link.dataset.baseHref || link.getAttribute("href");
      link.dataset.baseHref = base;
      const url = new URL(base, window.location.origin);
      if (rawQuery.trim().length > 0) {
        url.searchParams.set("query", rawQuery.trim());
      } else {
        url.searchParams.delete("query");
      }
      link.setAttribute("href", url.pathname + url.search);
    });
  }

  expand(event) {
    event.preventDefault();
    this.expandedValue = true;
    this.filter();
  }

  putNameMatchesFirst(tokens) {
    if (!this.searchesModelNames) return;

    const ranked = this.originalOrder.map((item, position) => ({
      item,
      position,
      rank: tokens.length === 0 || this.matchesName(item, tokens) ? 0 : 1,
    }));
    ranked.sort((a, b) => a.rank - b.rank || a.position - b.position);
    ranked.forEach(({ item }) => {
      item.parentElement.insertBefore(item, this.listEnds.get(item.parentElement));
    });
  }

  matchesName(item, tokens) {
    const { label } = this.searchTerms.get(item);
    return tokens.every((token) => label.includes(token));
  }

  matchesNameOrModel(item, tokens) {
    const { label, modelNames } = this.searchTerms.get(item);
    return tokens.every((token) => label.includes(token) || modelNames.some((name) => name.includes(token)));
  }

  searchTermsFor(item) {
    return {
      label: this.normalize(item.dataset.label || item.textContent),
      modelNames: JSON.parse(item.dataset.modelNames || "[]").map((name) => this.normalize(name)),
    };
  }

  isCollapsed(item) {
    return item.dataset.collapsed === "true" && !this.expandedValue;
  }

  tokenize(value) {
    return value
      .split(/\s+/)
      .map((token) => this.normalize(token))
      .filter((token) => token.length > 0);
  }

  normalize(value) {
    return value
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .replace(/[^a-z0-9]/g, "");
  }
}
